import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BannerResponse } from '../../../../models/BannersModels';
import { RootState } from '../../../../stores/Store';

import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import BannerReviewContent from '../../components/BannerReviewContent';
import BannerOverlapWarning from './BannerOverlapWarning';
import useBannersFormContainer from './useBannersFormContainer';

interface BannersFormReviewProps {
  editMode: boolean;
  onBack: () => void;
  onClose: () => void;
  onPublish: () => void;
  isLoading: boolean;
  overlappingBanners: BannerResponse[];
}

function BannersFormReview({
  editMode,
  onBack,
  onClose,
  onPublish,
  isLoading,
  overlappingBanners,
}: BannersFormReviewProps) {
  const [t] = useTranslation();
  const formData = useSelector((state: RootState) => state.bannersReducer.form);
  const { marketOptions, companiesOptions } = useBannersFormContainer();

  const marketNameById = useMemo(
    () => new Map(marketOptions.map((market) => [market.value, market.label])),
    [marketOptions],
  );
  const companyNameById = useMemo(
    () => new Map(companiesOptions.map((company) => [company.value, company.label])),
    [companiesOptions],
  );

  /**
   * Returns the publish button label
   * When there are overlapping banners the user must confirm publishing anyway
   */
  const getPublishLabel = () => {
    if (overlappingBanners.length > 0) return t('pages.bannerOnboarding.publishAnyway');
    return editMode ? t('common.saveChanges') : t('pages.bannerOnboarding.publish');
  };

  return (
    <Box sx={{ marginBottom: '3rem' }}>
      <Box
        sx={{
          boxShadow: '0px 0px 10px 1px rgba(0, 0, 0, 0.08)',
          borderRadius: '0.75rem',
          backgroundColor: (theme) => theme.palette.background.default,
          padding: '2.5rem',
        }}
      >
        {overlappingBanners.length > 0 ? <BannerOverlapWarning overlappingBanners={overlappingBanners} /> : null}
        <BannerReviewContent formData={formData} marketNameById={marketNameById} companyNameById={companyNameById} />
      </Box>

      <ButtonGroup sx={{ justifyContent: 'flex-end', paddingTop: '3rem' }}>
        <Button label={t('common.cancel')} color="alternative2Button" type="button" onClick={onClose} />
        <Button label={t('pages.bannerOnboarding.backToEdit')} color="secondaryButton" type="button" onClick={onBack} />
        <Button
          label={getPublishLabel()}
          color="primaryButton"
          type="button"
          onClick={onPublish}
          loading={isLoading}
          disabled={isLoading}
        />
      </ButtonGroup>
    </Box>
  );
}

export default BannersFormReview;
